import { useMemo, useState } from 'react'
import {
  FiChevronDown,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiHelpCircle,
  FiMail,
  FiMessageCircle,
  FiSearch,
  FiYoutube,
} from 'react-icons/fi'
import Card from './ui/Card'
import SectionTitle from './ui/SectionTitle'
import Reveal from './ui/Reveal'
import Button from './ui/Button'
import { FAQ_ITEMS, INSTALLATION_GUIDE } from '../config/faqData'

const QUICK_LINK_IDS = ['add-device', 'offline', 'wifi-reconfig', 'notifications']

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

function matchesQuery(item, query) {
  if (!query) return true
  const haystack = normalizeText(
    [item.question, item.answer, item.footer, item.category, ...(item.bullets || [])].join(' ')
  )
  return haystack.includes(normalizeText(query))
}

const FaqAnswer = ({ item }) => (
  <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-relaxed space-y-3">
    <p>{item.answer}</p>
    {item.bullets?.length > 0 && (
      <ul className="space-y-2 pl-1">
        {item.bullets.map((line) => (
          <li key={line} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent flex-shrink-0" aria-hidden />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    )}
    {item.footer && <p>{item.footer}</p>}
  </div>
)

const FAQ = () => {
  const [query, setQuery] = useState('')
  const [openId, setOpenId] = useState(FAQ_ITEMS[0]?.id || null)

  const filtered = useMemo(() => {
    const q = query.trim()
    return FAQ_ITEMS.filter((item) => matchesQuery(item, q))
  }, [query])

  const quickLinks = useMemo(
    () => QUICK_LINK_IDS.map((id) => FAQ_ITEMS.find((item) => item.id === id)).filter(Boolean),
    []
  )

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id))

  const jumpToQuestion = (id) => {
    setQuery('')
    setOpenId(id)
    requestAnimationFrame(() => {
      document.getElementById(`faq-trigger-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <section id="faq" className="section-padding relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(34, 198, 94, 0.06) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container-custom relative z-10">
        <Reveal direction="up">
          <SectionTitle title="Perguntas Frequentes" subtitle="Central de Ajuda" align="center" />
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] gap-6 lg:gap-8 items-start mt-6 lg:mt-8">
          <div className="min-w-0">
            <Reveal direction="right">
              <div className="mb-4">
                <label htmlFor="faq-search" className="sr-only">
                  Buscar por uma pergunta
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    id="faq-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar por uma pergunta..."
                    className="input-field !pl-12 !pr-4"
                    autoComplete="off"
                  />
                </div>
              </div>
            </Reveal>

            <div className="space-y-2.5">
              {filtered.length === 0 ? (
                <Reveal direction="up">
                  <Card padding="md" hover={false} className="text-center !p-5">
                    <p className="text-gray-600 dark:text-gray-300 font-medium">
                      Nenhuma pergunta encontrada para &quot;{query}&quot;.
                    </p>
                    <button
                      type="button"
                      onClick={() => setQuery('')}
                      className="mt-3 text-sm font-bold text-accent hover:underline"
                    >
                      Limpar busca
                    </button>
                  </Card>
                </Reveal>
              ) : (
                filtered.map((item, index) => {
                  const isOpen = openId === item.id
                  return (
                    <Reveal key={item.id} direction="up" delay={Math.min(index * 30, 180)}>
                      <div
                        className={`rounded-2xl border transition-all duration-300 overflow-hidden ${
                          isOpen
                            ? 'border-accent/40 bg-white dark:bg-gray-900 shadow-lg shadow-accent/5'
                            : 'border-gray-200 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 hover:border-accent/30'
                        }`}
                      >
                        <button
                          type="button"
                          id={`faq-trigger-${item.id}`}
                          aria-expanded={isOpen}
                          aria-controls={`faq-panel-${item.id}`}
                          onClick={() => toggle(item.id)}
                          className="w-full flex items-start gap-3 text-left px-4 py-3.5 focus:outline-none"
                        >
                          <span className="flex-1 min-w-0">
                            <span className="block text-[10px] sm:text-xs font-bold uppercase tracking-wider text-accent mb-1">
                              {item.category}
                            </span>
                            <span className="block text-sm sm:text-base font-extrabold text-gray-900 dark:text-white leading-snug">
                              {item.question}
                            </span>
                          </span>
                          <FiChevronDown
                            className={`w-5 h-5 text-accent flex-shrink-0 mt-1 transition-transform duration-300 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <div
                          id={`faq-panel-${item.id}`}
                          role="region"
                          aria-labelledby={`faq-trigger-${item.id}`}
                          className={`grid transition-all duration-300 ease-out ${
                            isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                          }`}
                        >
                          <div className="overflow-hidden">
                            <div className="px-4 pb-4 pt-0 border-t border-gray-100 dark:border-gray-800">
                              <div className="pt-3">
                                <FaqAnswer item={item} />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Reveal>
                  )
                })
              )}
            </div>
          </div>

          <aside className="min-w-0 lg:sticky lg:top-28 space-y-4">
            <Reveal direction="left" delay={80}>
              <Card
                padding="md"
                hover={false}
                className="border-2 border-accent/20 dark:border-accent/30 bg-gradient-to-br from-accent/5 via-white to-white dark:from-accent/10 dark:via-gray-900 dark:to-gray-900 !p-5 sm:!p-6"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="h-12 w-12 rounded-2xl bg-accent/15 dark:bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <FiHelpCircle className="w-6 h-6 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-1">Instalação</p>
                    <h3 className="text-base sm:text-lg font-extrabold text-gray-900 dark:text-white tracking-tight leading-snug">
                      {INSTALLATION_GUIDE.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      {INSTALLATION_GUIDE.description}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  <a href={INSTALLATION_GUIDE.pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex">
                    <Button variant="primary" size="md" className="w-full gap-2">
                      <FiDownload className="w-5 h-5" />
                      {INSTALLATION_GUIDE.pdfLabel}
                    </Button>
                  </a>
                  <a
                    href={INSTALLATION_GUIDE.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex"
                  >
                    <Button variant="secondary" size="md" className="w-full gap-2">
                      <FiYoutube className="w-5 h-5" />
                      {INSTALLATION_GUIDE.youtubeLabel}
                    </Button>
                  </a>
                </div>
              </Card>
            </Reveal>

            <Reveal direction="left" delay={120}>
              <Card padding="md" hover={false} className="!p-5 sm:!p-6">
                <p className="text-xs font-bold text-accent uppercase tracking-[0.2em] mb-3">Atalhos rápidos</p>
                <ul className="space-y-1">
                  {quickLinks.map((item) => (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => jumpToQuestion(item.id)}
                        className="group w-full flex items-center gap-3 rounded-xl py-2 text-left transition-colors hover:bg-accent/5 dark:hover:bg-accent/10"
                      >
                        <FiChevronRight className="w-4 h-4 text-accent flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
                        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-snug">
                          {item.question}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            </Reveal>

            <Reveal direction="left" delay={160}>
              <Card padding="md" hover={false} className="relative overflow-hidden !p-5 sm:!p-6">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent/60 via-accent to-accent/60" aria-hidden />
                <div className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-accent/10 dark:bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <FiMessageCircle className="w-5 h-5 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-extrabold text-gray-900 dark:text-white">Precisa de ajuda?</h3>
                    <p className="mt-1.5 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      Não encontrou o que procurava? Nossa equipe responde em até 24 horas úteis.
                    </p>
                    <div className="mt-4 flex flex-col items-start gap-3">
                      <a
                        href="mailto:sysmv.contato@gmail.com"
                        className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:underline break-all "
                      >
                        <FiMail className="w-4 h-4 flex-shrink-0 " />
                        sysmv.contato@gmail.com
                      </a>
                      <button
                        type="button"
                        onClick={scrollToContact}
                        className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-700 dark:text-gray-200 hover:text-accent dark:hover:text-accent transition-colors"
                      >
                        Enviar mensagem
                        <FiExternalLink className="w-4 h-4 flex-shrink-0" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </Reveal>
          </aside>
        </div>
      </div>
    </section>
  )
}

export default FAQ
